import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import AppShell from '../components/AppShell';

// Глобальні налаштування системи (спільні для всіх точок). Поки що — правила бонусів.
export default function SettingsPage() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [earn, setEarn] = useState('');
  const [maxSpend, setMaxSpend] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  // Фіскалізація (Checkbox)
  const [fiscal, setFiscal] = useState({ enabled:false, provider:'checkbox', api_url:'https://api.checkbox.ua', license_key:'', cashier_login:'', cashier_pin:'' });
  const [fiscalSaving, setFiscalSaving] = useState(false);
  const [fiscalMsg, setFiscalMsg] = useState('');

  // AI — розпізнавання накладних (Anthropic)
  const [ai, setAi] = useState({ anthropic_api_key:'', scan_model:'claude-opus-4-8' });
  const [aiSaving, setAiSaving] = useState(false);
  const [aiMsg, setAiMsg] = useState('');

  // Лише власник мережі має доступ до системних налаштувань.
  useEffect(() => {
    if (!isSuperAdmin) { navigate('/dashboard'); return; }
    Promise.all([
      api.get('/settings/bonus').then(r => { setEarn(String(r.data.earn_percent)); setMaxSpend(String(r.data.max_spend_percent)); }),
      api.get('/settings/fiscal').then(r => setFiscal(r.data)).catch(() => {}),
      api.get('/settings/ai').then(r => setAi(r.data)).catch(() => {}),
    ])
      .catch(() => setError('Не вдалося завантажити налаштування'))
      .finally(() => setLoading(false));
  }, [isSuperAdmin, navigate]);

  async function saveAi(e) {
    e.preventDefault();
    setAiSaving(true); setAiMsg('');
    try {
      const res = await api.put('/settings/ai', ai);
      setAi(res.data);
      setAiMsg('✓ Збережено');
    } catch (err) {
      setAiMsg(err.response?.data?.detail || 'Не вдалося зберегти');
    } finally { setAiSaving(false); }
  }

  async function saveFiscal(e) {
    e.preventDefault();
    setFiscalSaving(true); setFiscalMsg('');
    try {
      const res = await api.put('/settings/fiscal', fiscal);
      setFiscal(res.data);
      setFiscalMsg('✓ Збережено');
    } catch (err) {
      setFiscalMsg(err.response?.data?.detail || 'Не вдалося зберегти');
    } finally { setFiscalSaving(false); }
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true); setMsg(''); setError('');
    try {
      const res = await api.put('/settings/bonus', {
        earn_percent: parseFloat(earn) || 0,
        max_spend_percent: parseFloat(maxSpend) || 0,
      });
      setEarn(String(res.data.earn_percent));
      setMaxSpend(String(res.data.max_spend_percent));
      setMsg('✓ Збережено — застосовується одразу до нових продажів');
    } catch (err) {
      setError(err.response?.data?.detail || 'Не вдалося зберегти');
    } finally { setSaving(false); }
  }

  return (
    <AppShell title="Налаштування" subtitle="Параметри системи (всі точки)" onBack={() => navigate('/dashboard')}>
      <div className="dash-section" style={{ maxWidth: 520 }}>
        <h2 className="section-title">🎁 Бонусна програма</h2>
        <p className="muted" style={{ marginTop: -6, marginBottom: 16 }}>
          Правила діють для всіх магазинів мережі.
        </p>

        {loading ? (
          <div className="hint">Завантаження…</div>
        ) : (
          <form onSubmit={save} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="field">
              <label className="label">Нарахування бонусів, % від суми чека</label>
              <input type="number" step="0.1" min="0" max="100" value={earn}
                onChange={e => setEarn(e.target.value)} required />
              <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Напр. 1 → клієнту нараховується 1% від чека.
              </p>
            </div>

            <div className="field">
              <label className="label">Максимальне списання бонусів, % від суми чека</label>
              <input type="number" step="0.1" min="0" max="100" value={maxSpend}
                onChange={e => setMaxSpend(e.target.value)} required />
              <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Напр. 30 → бонусами можна оплатити не більше 30% чека.
              </p>
            </div>

            {error && <div className="hint" style={{ color: '#ff6b6b' }}>{error}</div>}
            {msg && <div className="hint" style={{ color: '#5fbf8f' }}>{msg}</div>}

            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Збереження…' : 'Зберегти'}
            </button>
          </form>
        )}
      </div>

      <div className="dash-section" style={{ maxWidth: 520 }}>
        <h2 className="section-title">🧾 Фіскалізація (Checkbox)</h2>
        <p className="muted" style={{ marginTop: -6, marginBottom: 16 }}>
          Спільне для всієї мережі. Каса, касир і License Key — у налаштуваннях <b>кожного магазину</b>.
        </p>
        {!loading && (
          <form onSubmit={saveFiscal} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="field">
              <label className="label">Checkbox API URL (тест / бій)</label>
              <input value={fiscal.api_url}
                onChange={e => setFiscal(f => ({ ...f, api_url: e.target.value }))}
                placeholder="https://api.checkbox.ua" />
              <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Бойовий: https://api.checkbox.ua · тестовий: https://dev-api.checkbox.in.ua
              </p>
            </div>

            {fiscalMsg && <div className="hint" style={{ color: fiscalMsg.startsWith('✓') ? '#5fbf8f' : '#ff6b6b' }}>{fiscalMsg}</div>}
            <button className="btn btn-primary" type="submit" disabled={fiscalSaving}>
              {fiscalSaving ? 'Збереження…' : 'Зберегти'}
            </button>
          </form>
        )}
      </div>

      <div className="dash-section" style={{ maxWidth: 520 }}>
        <h2 className="section-title">📷 Розпізнавання накладних (AI)</h2>
        <p className="muted" style={{ marginTop: -6, marginBottom: 16 }}>
          Ключ для кнопки «Сканувати накладну» на складі. Розпізнавання фото накладної в позиції приходу.
        </p>
        {!loading && (
          <form onSubmit={saveAi} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="field">
              <label className="label">API-ключ Anthropic</label>
              <input type="password" autoComplete="new-password" value={ai.anthropic_api_key}
                onChange={e => setAi(a => ({ ...a, anthropic_api_key: e.target.value }))}
                placeholder="sk-ant-..." />
              <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                Де взяти: console.anthropic.com → увійти → Billing (поповнити баланс) → API Keys → Create Key.
                Ключ починається на <b>sk-ant-</b>. Вартість ≈ 1–3 центи за накладну.
              </p>
            </div>
            <div className="field">
              <label className="label">Модель</label>
              <input value={ai.scan_model}
                onChange={e => setAi(a => ({ ...a, scan_model: e.target.value }))}
                placeholder="claude-opus-4-8" />
              <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                За замовчуванням claude-opus-4-8 (найточніше). Дешевше/швидше: claude-haiku-4-5.
              </p>
            </div>
            {aiMsg && <div className="hint" style={{ color: aiMsg.startsWith('✓') ? '#5fbf8f' : '#ff6b6b' }}>{aiMsg}</div>}
            <button className="btn btn-primary" type="submit" disabled={aiSaving}>
              {aiSaving ? 'Збереження…' : 'Зберегти'}
            </button>
          </form>
        )}
      </div>
    </AppShell>
  );
}
